import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, Save, Loader2, Search } from 'lucide-react';
import { useAsset, useCreateAsset, useUpdateAsset } from '@/api/hooks/useAssets';
import { useLocationsDropdown } from '@/api/hooks/useLocations';
import { useAvailableEpcs } from '@/api/hooks/useEpcs';
import { useAssetClassesDropdown } from '@/api/hooks/useCatalogs';
import {
  useMetadataSchemasForAssetClass,
  useMetadataCategoriesDropdown,
  useAssetMetadata,
  useSaveAssetMetadata,
} from '@/api/hooks/useMetadata';
import DynamicMetadataForm, { DynamicMetadataFormSkeleton } from '@/components/metadata/DynamicMetadataForm';
import { AssetImageManager } from './components/AssetImageManager';
import type { MetadataValue } from '@/types/metadata.types';
import { tenantConfig, getAssetStatusLabel } from '@/config/tenant';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/use-permissions';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';

interface FormData {
  assetId: string;
  name: string;
  status: string;
  epc: string;
  description: string;
  locationId: string;
  assetClassId: string;
  comments: string;
  isActive: boolean;
}

const initialFormData: FormData = {
  assetId: '',
  name: '',
  status: '1', // Registrado by default
  epc: '',
  description: '',
  locationId: '',
  assetClassId: '',
  comments: '',
  isActive: true,
};

export default function AssetFormPage() {
  const { t } = useTranslation(['assets', 'common']);
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  // Feature 3: when a user has "activos.editMetadata" but NOT "activos.edit"
  // at FullAccess, they can only change the dynamic metadata fields on
  // existing assets. Non-metadata controls are disabled and the asset
  // save endpoint is skipped (only metadata saves are fired).
  // Module is 'activos' (Spanish) — matches the existing convention in the
  // Permission table seed data; 'assets' is NOT a valid module key.
  const { canEdit, hasPermission } = usePermissions();
  const canEditAsset = canEdit('activos');
  const canEditMetadata = canEditAsset || hasPermission('activos', 'editMetadata', 1);
  const metadataOnly = isEditing && canEditMetadata && !canEditAsset;

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [epcSearch, setEpcSearch] = useState('');
  const [showEpcDropdown, setShowEpcDropdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metadataValues, setMetadataValues] = useState<MetadataValue[]>([]);
  // Feature 1: per-schema validity and per-schema error maps. The overall
  // form is valid only when EVERY schema reports valid.
  const [metadataValidBySchema, setMetadataValidBySchema] = useState<Record<number, boolean>>({});
  const [metadataErrorsBySchema, setMetadataErrorsBySchema] = useState<Record<number, Record<string, string>>>({});

  // Fetch existing asset when editing
  const { data: existingAsset, isLoading: assetLoading } = useAsset(id || '', isEditing);

  // Fetch locations for dropdown
  const { data: locations, isLoading: locationsLoading } = useLocationsDropdown();

  // Fetch available EPCs for selection
  const { data: availableEpcs, isLoading: epcsLoading } = useAvailableEpcs();

  // Fetch asset classes for dropdown
  const { data: assetClasses, isLoading: assetClassesLoading } = useAssetClassesDropdown();

  // Feature 1: fetch the full list of metadata schemas linked to the
  // selected asset class (M:N). Each schema renders its own form; values
  // are merged by fieldKey which is globally unique across schemas
  // (the backend rejects schema sets with duplicate codes).
  const assetClassIdNum = formData.assetClassId ? parseInt(formData.assetClassId) : 0;
  const { data: metadataSchemas, isLoading: schemaLoading } = useMetadataSchemasForAssetClass(
    assetClassIdNum,
    assetClassIdNum > 0
  );

  // Fetch metadata categories for grouping
  const { data: metadataCategories } = useMetadataCategoriesDropdown();

  // Fetch existing asset metadata when editing
  const { data: existingMetadata } = useAssetMetadata(id || '', isEditing);

  // Mutations
  const createAsset = useCreateAsset();
  const saveAssetMetadata = useSaveAssetMetadata();
  const updateAsset = useUpdateAsset();

  // Filter EPCs based on search
  const filteredEpcs = useMemo(() => {
    if (!availableEpcs) return [];
    if (!epcSearch.trim()) return availableEpcs.slice(0, 50); // Limit initial display
    const search = epcSearch.toLowerCase();
    return availableEpcs.filter(epc =>
      epc.code.toLowerCase().includes(search)
    ).slice(0, 50);
  }, [availableEpcs, epcSearch]);

  // Load existing asset data when editing
  useEffect(() => {
    if (existingAsset && isEditing) {
      setFormData({
        assetId: String(existingAsset.assetId),
        name: existingAsset.name || '',
        status: String(existingAsset.assetStatus) || '1',
        epc: existingAsset.epc || '',
        description: existingAsset.description || '',
        locationId: existingAsset.locationId ? String(existingAsset.locationId) : '',
        assetClassId: existingAsset.assetClassId ? String(existingAsset.assetClassId) : '',
        comments: existingAsset.comments || '',
        isActive: existingAsset.isActive ?? true,
      });
    }
  }, [existingAsset, isEditing]);

  // Load existing metadata values when editing
  useEffect(() => {
    if (existingMetadata?.values && isEditing) {
      setMetadataValues(existingMetadata.values);
    }
  }, [existingMetadata, isEditing]);

  // Feature 1: per-schema change handler. The sub-form only sees/edits values
  // for its own fields; we merge those back into the global metadata set by
  // fieldKey (keeping values from other schemas intact).
  const handleSchemaChange = useCallback(
    (schemaFieldKeys: Set<string>, schemaValues: MetadataValue[]) => {
      setMetadataValues((prev) => {
        const untouched = prev.filter((v) => !schemaFieldKeys.has(v.fieldKey));
        return [...untouched, ...schemaValues];
      });
    },
    []
  );

  const handleSchemaValidationChange = useCallback(
    (schemaId: number, isValid: boolean, errors: Record<string, string>) => {
      setMetadataValidBySchema((prev) => ({ ...prev, [schemaId]: isValid }));
      setMetadataErrorsBySchema((prev) => ({ ...prev, [schemaId]: errors }));
    },
    []
  );

  // Aggregate validation across all schemas.
  const metadataValid = useMemo(
    () => Object.values(metadataValidBySchema).every((v) => v),
    [metadataValidBySchema]
  );
  const firstMetadataError = useMemo(() => {
    for (const errs of Object.values(metadataErrorsBySchema)) {
      const first = Object.values(errs)[0];
      if (first) return first;
    }
    return null;
  }, [metadataErrorsBySchema]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.assetId.trim()) {
      setError(t('validation.assetIdRequired'));
      return;
    }
    if (!formData.name.trim()) {
      setError(t('validation.nameRequired'));
      return;
    }
    if (!formData.locationId) {
      setError(t('validation.locationRequired'));
      return;
    }

    // Validate metadata if any schema is linked
    if (metadataSchemas && metadataSchemas.length > 0 && !metadataValid) {
      setError(firstMetadataError || t('validation.metadataInvalid', 'Please fix the errors in the metadata fields'));
      return;
    }

    try {
      const payload = {
        assetId: isEditing ? id : formData.assetId,
        name: formData.name,
        description: formData.description,
        locationId: parseInt(formData.locationId),
        assetClassId: formData.assetClassId ? parseInt(formData.assetClassId) : undefined,
        status: parseInt(formData.status),
        notes: formData.comments,
      };

      // Feature 3: metadata-only users skip the core asset save entirely;
      // the backend rejects it anyway, but not firing it produces a cleaner
      // UX (no trailing 403 toast after a successful metadata save).
      // For create flows, capture the actual saved assetId from the server
      // response — the backend may have generated a hex id when the user
      // left the field blank, and the metadata POST has to target the real id.
      let savedAssetId: string | undefined;
      if (!metadataOnly) {
        if (isEditing) {
          await updateAsset.mutateAsync({ id: id!, data: payload });
        } else {
          savedAssetId = await createAsset.mutateAsync(payload);
        }
      }

      // Feature 1: save metadata per schema. Each schema's values go to its
      // own /api/metadata/assets/{id} POST call (keyed by metadataSchemaId).
      if (metadataSchemas && metadataSchemas.length > 0) {
        const assetIdToSave = isEditing ? id! : (savedAssetId ?? formData.assetId);
        for (const schema of metadataSchemas) {
          if (!schema.metadataSchemaId) continue;
          const schemaFieldKeys = new Set(schema.fields.map((f) => f.fieldKey));
          const subset = metadataValues.filter((v) => schemaFieldKeys.has(v.fieldKey));
          await saveAssetMetadata.mutateAsync({
            assetId: assetIdToSave,
            metadataSchemaId: schema.metadataSchemaId,
            values: subset,
          });
        }
      }

      navigate('/assets');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('messages.errorSaving'));
    }
  };

  const handleSelectEpc = (epcCode: string) => {
    setFormData(prev => ({ ...prev, epc: epcCode }));
    setEpcSearch('');
    setShowEpcDropdown(false);
  };

  const handleClearEpc = () => {
    setFormData(prev => ({ ...prev, epc: '' }));
  };

  const isLoading = createAsset.isPending || updateAsset.isPending || saveAssetMetadata.isPending;
  const isPageLoading = isEditing && assetLoading;

  if (isPageLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <Skeleton className="h-[600px] max-w-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/assets">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEditing ? t('form.editTitle') : t('form.createTitle')}
          </h1>
          <p className="text-muted-foreground">
            {isEditing ? t('form.editDescription') : t('form.createDescription')}
          </p>
        </div>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>{t('form.cardTitle')}</CardTitle>
          <CardDescription>
            {t('form.cardDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            {metadataOnly && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  {t('form.metadataOnlyBanner', {
                    defaultValue:
                      'You can only edit the custom metadata fields for this asset. Other fields are read-only.',
                  })}
                </AlertDescription>
              </Alert>
            )}

            {/* Asset ID */}
            <div className="space-y-2">
              <Label htmlFor="assetId">{t('form.assetIdLabel')} *</Label>
              <Input
                id="assetId"
                placeholder={t('form.assetIdPlaceholder')}
                value={formData.assetId}
                onChange={(e) => setFormData(prev => ({ ...prev, assetId: e.target.value }))}
                disabled={isEditing || metadataOnly}
                className={isEditing ? 'bg-muted' : ''}
                required
              />
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">{t('form.nameLabel')} *</Label>
              <Input
                id="name"
                placeholder={t('form.namePlaceholder')}
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                disabled={metadataOnly}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Status */}
              <div className="space-y-2">
                <Label htmlFor="status">{t('form.statusLabel')} *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}
                  disabled={metadataOnly}
                >
                  <SelectTrigger id="status">
                    <SelectValue placeholder={t('form.statusPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {tenantConfig.assetStatuses.map((status) => (
                      <SelectItem key={status.value} value={String(status.value)}>
                        {getAssetStatusLabel(status.value)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="location">{t('form.locationLabel')} *</Label>
                <Select
                  value={formData.locationId || 'none'}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, locationId: v === 'none' ? '' : v }))}
                  disabled={metadataOnly}
                >
                  <SelectTrigger id="location">
                    <SelectValue placeholder={locationsLoading ? t('form.locationLoading') : t('form.locationPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('form.locationPlaceholder')}</SelectItem>
                    {locations?.map((location) => (
                      <SelectItem key={location.id} value={String(location.id)}>
                        {location.name}
                        {location.type && ` (${location.type})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Asset Class */}
            <div className="space-y-2">
              <Label htmlFor="assetClass">{t('form.assetClassLabel', 'Asset Class')}</Label>
              <Select
                value={formData.assetClassId || 'none'}
                onValueChange={(v) => {
                  setFormData(prev => ({ ...prev, assetClassId: v === 'none' ? '' : v }));
                  // Clear metadata values when asset class changes
                  if (v === 'none' || v !== formData.assetClassId) {
                    setMetadataValues([]);
                  }
                }}
                disabled={metadataOnly}
              >
                <SelectTrigger id="assetClass">
                  <SelectValue placeholder={assetClassesLoading ? t('form.assetClassLoading', 'Loading...') : t('form.assetClassPlaceholder', 'Select an asset class')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('form.assetClassPlaceholder', 'Select an asset class')}</SelectItem>
                  {assetClasses?.map((assetClass) => (
                    <SelectItem key={assetClass.value} value={String(assetClass.value)}>
                      {assetClass.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('form.assetClassHint', 'Select an asset class to display additional custom fields')}
              </p>
            </div>

            {/* EPC Selection */}
            <div className="space-y-2">
              <Label htmlFor="epc">{t('form.epcLabel')}</Label>
              {formData.epc ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={formData.epc}
                    disabled
                    className="bg-muted font-mono"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleClearEpc}
                  >
                    {t('form.changeEpcButton')}
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="epc"
                      placeholder={t('form.epcSearchPlaceholder')}
                      value={epcSearch}
                      onChange={(e) => {
                        setEpcSearch(e.target.value);
                        setShowEpcDropdown(e.target.value.length >= 3);
                      }}
                      onFocus={() => epcSearch.length >= 3 && setShowEpcDropdown(true)}
                      className="pl-8"
                    />
                  </div>
                  {showEpcDropdown && (
                    <div className="absolute z-10 mt-1 w-full rounded-md border bg-popover shadow-md">
                      <ScrollArea className="h-[200px]">
                        {epcsLoading ? (
                          <div className="flex items-center justify-center p-4">
                            <Loader2 className="h-4 w-4 animate-spin" />
                          </div>
                        ) : filteredEpcs.length === 0 ? (
                          <div className="p-4 text-center text-sm text-muted-foreground">
                            {t('form.epcNotFound')}
                          </div>
                        ) : (
                          <div className="p-1">
                            {filteredEpcs.map((epc) => (
                              <button
                                key={epc.id}
                                type="button"
                                onClick={() => handleSelectEpc(epc.code)}
                                className={cn(
                                  'w-full px-3 py-2 text-left text-sm rounded-sm hover:bg-accent',
                                  'font-mono'
                                )}
                              >
                                {epc.code}
                              </button>
                            ))}
                          </div>
                        )}
                      </ScrollArea>
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {t('form.epcHint')}
              </p>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">{t('form.descriptionLabel')}</Label>
              <Input
                id="description"
                placeholder={t('form.descriptionPlaceholder')}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                disabled={metadataOnly}
              />
            </div>

            {/* Comments */}
            <div className="space-y-2">
              <Label htmlFor="comments">{t('form.commentsLabel')}</Label>
              <Textarea
                id="comments"
                placeholder={t('form.commentsPlaceholder')}
                value={formData.comments}
                onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                rows={3}
                disabled={metadataOnly}
              />
            </div>

            {/* Active checkbox */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) =>
                  setFormData(prev => ({ ...prev, isActive: checked === true }))
                }
              />
              <Label htmlFor="isActive" className="text-sm font-normal">
                {t('form.activeLabel')}
              </Label>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('form.savingButton')}
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {t('form.saveButton')}
                  </>
                )}
              </Button>
              <Link to="/assets">
                <Button type="button" variant="outline" disabled={isLoading}>
                  {t('common:buttons.cancel')}
                </Button>
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Feature 1: one card per linked metadata schema. */}
      {formData.assetClassId && (
        schemaLoading ? (
          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle>{t('form.metadataTitle', 'Additional Information')}</CardTitle>
            </CardHeader>
            <CardContent>
              <DynamicMetadataFormSkeleton />
            </CardContent>
          </Card>
        ) : metadataSchemas && metadataSchemas.length > 0 ? (
          metadataSchemas.map((schema) => {
            const schemaId = schema.metadataSchemaId ?? 0;
            const fieldKeys = new Set(schema.fields.map((f) => f.fieldKey));
            const schemaSubset = metadataValues.filter((v) => fieldKeys.has(v.fieldKey));
            return (
              <Card key={schemaId} className="max-w-2xl">
                <CardHeader>
                  <CardTitle>{schema.name}</CardTitle>
                  {schema.description && (
                    <CardDescription>{schema.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <DynamicMetadataForm
                    schema={schema}
                    values={schemaSubset}
                    categories={metadataCategories}
                    onChange={(values) => handleSchemaChange(fieldKeys, values)}
                    onValidationChange={(isValid, errors) =>
                      handleSchemaValidationChange(schemaId, isValid, errors)
                    }
                    showCategories={true}
                  />
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle>{t('form.metadataTitle', 'Additional Information')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {t('form.noMetadataSchema', 'No custom fields defined for this asset class')}
              </p>
            </CardContent>
          </Card>
        )
      )}

      {/* Asset Images - Only when editing */}
      {isEditing && id && (
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>{t('images.title', 'Images')}</CardTitle>
            <CardDescription>
              {t('images.managerDescription', 'Upload and manage images for this asset')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AssetImageManager assetId={id} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
